import { useState } from "react";
import { useCall, formatDuration } from "@/context/CallContext";
import { useAuth } from "@/context/AuthContext";
import {
  Phone, PhoneSlash, MicrophoneSlash, Microphone, Pause, Play,
  Record as RecordFill, Plus, Hash, Asterisk, X, Minus,
  ArrowsLeftRight, UsersThree, ArrowRight,
} from "@phosphor-icons/react";

const KEYS = [
  ["1", ""],["2","ABC"],["3","DEF"],
  ["4","GHI"],["5","JKL"],["6","MNO"],
  ["7","PQRS"],["8","TUV"],["9","WXYZ"],
  ["*",""],["0","+"],["#",""],
];

export default function RightDialer() {
  const { user } = useAuth();
  const {
    activeCall, duration, muted, held, recording, dialerOpen, deviceReady, incomingCall,
    setMuted, setHeld, setRecording, setDialerOpen, startCall, endCall,
    acceptIncoming, rejectIncoming, sendDigits, transferCall, addConferenceParticipant,
  } = useCall();
  const [number, setNumber] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [overlay, setOverlay] = useState(null); // 'transfer' | 'conference' | null

  const press = (k) => {
    setNumber((n) => n + k);
    if (activeCall) sendDigits(k); // DTMF during active call
  };

  const handleCall = async () => {
    if (!number.trim()) return;
    await startCall({ to_number: number, to_name: "Unknown" });
  };

  if (!dialerOpen && !activeCall) {
    // No floating action button — dialer is accessed from the sidebar "Dialer" page
    // or auto-opens when an active call starts.
    return null;
  }

  // Personal comms features are locked until the user has their own seat.
  // Owner (platform staff) bypasses; there is no floating dialer for them.
  if (user && user.role !== "owner" && !user.has_own_seat) {
    return null;
  }

  return (
    <aside
      className="w-[340px] bg-white border-l border-[var(--border)] flex flex-col"
      data-testid="right-dialer-panel"
    >
      {/* iter135: pr-14 keeps the Close/Minimize buttons clear of the
          fixed NotificationBell (top-4 right-6 z-40) which was sitting
          on top of them on smaller screens. */}
      <div className="px-4 py-3 pr-14 border-b border-[var(--border)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Phone size={16} className="text-[var(--accent)]" />
          <span className="font-display font-bold text-[14px] tracking-wide">{activeCall ? "ACTIVE CALL" : "DIALER"}</span>
          <span className={`w-1.5 h-1.5 rounded-full ${deviceReady ? "bg-[var(--success)]" : "bg-[var(--text-muted)]"}`} title={deviceReady ? "Twilio device ready" : "Connecting..."} data-testid="twilio-status-dot" />
        </div>
        <div className="flex gap-1 relative z-[41]">
          <button onClick={() => setCollapsed(c => !c)} className="p-1.5 hover:bg-[var(--bg-sidebar)]" data-testid="dialer-collapse">
            <Minus size={14} />
          </button>
          {!activeCall && (
            <button onClick={() => setDialerOpen(false)} className="p-1.5 hover:bg-[var(--bg-sidebar)]" data-testid="dialer-close">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {!collapsed && (
        <>
          {activeCall ? (
            <ActiveCallView
              activeCall={activeCall} duration={duration}
              muted={muted} held={held} recording={recording}
              setMuted={setMuted} setHeld={setHeld} setRecording={setRecording}
              endCall={endCall}
              overlay={overlay} setOverlay={setOverlay}
              transferCall={transferCall} addConferenceParticipant={addConferenceParticipant}
            />
          ) : (
            <DialPad
              number={number} setNumber={setNumber}
              press={press} onCall={handleCall}
            />
          )}
        </>
      )}
    </aside>
  );
}

function DialPad({ number, setNumber, press, onCall }) {
  return (
    <div className="flex-1 flex flex-col items-center px-5 py-5">
      <div className="w-full mb-4">
        <input
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          placeholder="Enter number"
          className="input font-mono text-[22px] text-center tracking-wider"
          data-testid="dialer-input"
        />
      </div>
      <div className="grid grid-cols-3 gap-2.5 mb-5">
        {KEYS.map(([k, s]) => (
          <button
            key={k}
            onClick={() => press(k)}
            className="dial-key"
            data-testid={`dial-key-${k}`}
          >
            <span>{k}</span>
            {s && <span className="sub">{s}</span>}
          </button>
        ))}
      </div>
      <div className="flex gap-3 w-full">
        <button
          onClick={() => setNumber("")}
          className="btn-ghost flex-1"
          data-testid="dialer-clear"
        >
          Clear
        </button>
        <button
          onClick={onCall}
          disabled={!number.trim()}
          className="flex-1 flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#059669] disabled:opacity-50 text-white font-semibold py-3"
          data-testid="dialer-call-btn"
        >
          <Phone size={18} weight="fill" />
          Call
        </button>
      </div>
    </div>
  );
}

function ActiveCallView({ activeCall, duration, muted, held, recording, setMuted, setHeld, setRecording, endCall, overlay, setOverlay, transferCall, addConferenceParticipant }) {
  return (
    <div className="flex-1 flex flex-col items-center px-5 py-6 relative">
      <div className="relative mb-4">
        <div className={`w-24 h-24 rounded-full bg-[var(--bg-sidebar)] border-2 border-[var(--accent)] flex items-center justify-center font-display font-extrabold text-[28px] text-[var(--accent)] ${held ? "" : "ring-pulse"}`}
          data-testid="active-call-avatar">
          {(activeCall.to_name || "?").slice(0, 1).toUpperCase()}
        </div>
      </div>
      <div className="font-display text-[19px] font-bold text-center" data-testid="active-call-name">
        {activeCall.to_name || "Unknown"}
      </div>
      <div className="font-mono text-[14px] text-[var(--text-secondary)] mt-1" data-testid="active-call-number">
        {activeCall.to_number}
      </div>

      <div className="font-mono text-[40px] tracking-wider mt-5 text-[var(--text-primary)]" data-testid="active-call-duration">
        {formatDuration(duration)}
      </div>
      <div className="text-[11px] tracking-widest text-[var(--text-muted)] mt-1">
        {held ? "ON HOLD" : (muted ? "MUTED" : "CONNECTED")}
      </div>

      {/* Waveform */}
      <div className="flex items-center h-10 mt-4 mb-4">
        {Array.from({ length: 28 }).map((_, i) => (
          <span
            key={`wave-bar-${i}`}
            className="wave-bar"
            style={{
              height: `${20 + ((i * 13) % 18)}px`,
              animationDelay: `${(i * 60) % 800}ms`,
              opacity: held ? 0.25 : 1,
            }}
          />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2 w-full mb-3">
        <ActionBtn active={muted} onClick={() => setMuted(!muted)} icon={muted ? MicrophoneSlash : Microphone} label={muted ? "Unmute" : "Mute"} testid="call-mute" />
        <ActionBtn active={held} onClick={() => setHeld(!held)} icon={held ? Play : Pause} label={held ? "Resume" : "Hold"} testid="call-hold" />
        <ActionBtn active={recording} onClick={() => setRecording(!recording)} icon={RecordFill} label={recording ? "Rec ON" : "Record"} danger={recording} testid="call-record" />
      </div>

      <div className="grid grid-cols-2 gap-2 w-full mb-4">
        <ActionBtn onClick={() => setOverlay("transfer")} icon={ArrowsLeftRight} label="Transfer" testid="call-transfer-btn" />
        <ActionBtn onClick={() => setOverlay("conference")} icon={UsersThree} label="Add to Call" testid="call-conference-btn" />
      </div>

      <button
        onClick={() => endCall("completed")}
        className="w-full bg-[var(--danger)] hover:bg-[#DC2626] text-white py-3 flex items-center justify-center gap-2 font-semibold"
        data-testid="call-hangup-btn"
      >
        <PhoneSlash size={18} weight="fill" />
        End Call
      </button>

      {overlay === "transfer" && (
        <TargetPanel
          title="Transfer call"
          subtitle="Customer's caller ID is preserved on the transferred leg."
          placeholder="+1 555 123 4567 or username"
          ctaLabel="Transfer"
          submitTestId="transfer-submit"
          onClose={() => setOverlay(null)}
          onSubmit={async (target, mode) => {
            await transferCall(target, mode);
            setOverlay(null);
          }}
          withMode
        />
      )}
      {overlay === "conference" && (
        <TargetPanel
          title="Add to conference"
          subtitle="Bring a third party onto this call (3-way)."
          placeholder="+1 555 123 4567"
          ctaLabel="Add"
          submitTestId="conference-submit"
          onClose={() => setOverlay(null)}
          onSubmit={async (target) => {
            await addConferenceParticipant(target);
            setOverlay(null);
          }}
        />
      )}
    </div>
  );
}

function TargetPanel({ title, subtitle, placeholder, ctaLabel, submitTestId, onClose, onSubmit, withMode }) {
  const [target, setTarget] = useState("");
  const [mode, setMode] = useState("cold");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!target.trim()) return;
    setBusy(true);
    try { await onSubmit(target.trim(), mode); } finally { setBusy(false); }
  };
  return (
    <div className="absolute inset-0 bg-white flex flex-col" data-testid="call-action-overlay">
      <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-widest text-[var(--text-muted)] font-semibold">ACTIVE CALL</div>
          <div className="font-display font-bold text-[15px]">{title}</div>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-[var(--bg-sidebar)]" data-testid="call-action-close">
          <X size={14} />
        </button>
      </div>
      <div className="flex-1 px-5 py-5">
        <p className="text-[12px] text-[var(--text-secondary)] mb-3">{subtitle}</p>
        <label className="block text-[11px] tracking-widest font-semibold text-[var(--text-muted)] mb-1">TARGET</label>
        <input
          autoFocus
          value={target}
          onChange={e => setTarget(e.target.value)}
          placeholder={placeholder}
          className="input font-mono"
          data-testid="call-action-target"
        />
        {withMode && (
          <div className="mt-4">
            <label className="block text-[11px] tracking-widest font-semibold text-[var(--text-muted)] mb-1.5">MODE</label>
            <div className="flex gap-2">
              <button
                onClick={() => setMode("cold")}
                className={`flex-1 py-2 text-[12px] font-semibold border ${mode==="cold" ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "bg-white border-[var(--border)]"}`}
                data-testid="call-action-mode-cold"
              >
                Cold — hand off now
              </button>
              <button
                onClick={() => setMode("warm")}
                className={`flex-1 py-2 text-[12px] font-semibold border ${mode==="warm" ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "bg-white border-[var(--border)]"}`}
                data-testid="call-action-mode-warm"
              >
                Warm — talk first
              </button>
            </div>
          </div>
        )}
        <button
          onClick={submit}
          disabled={!target.trim() || busy}
          className="mt-5 w-full bg-[#10B981] hover:bg-[#059669] disabled:opacity-50 text-white font-semibold py-2.5 flex items-center justify-center gap-2"
          data-testid={submitTestId}
        >
          <ArrowRight size={16} weight="bold" /> {busy ? "Working…" : ctaLabel}
        </button>
      </div>
    </div>
  );
}

function ActionBtn({ active, onClick, icon: Icon, label, danger, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex flex-col items-center justify-center py-3 border transition-colors ${
        active
          ? danger
            ? "bg-[var(--danger)] text-white border-[var(--danger)]"
            : "bg-[var(--accent)] text-white border-[var(--accent)]"
          : "bg-white text-[var(--text-primary)] border-[var(--border)] hover:bg-[var(--bg-sidebar)]"
      }`}
    >
      <Icon size={18} weight={active ? "fill" : "regular"} />
      <span className="text-[10px] mt-1 tracking-wide font-semibold">{label}</span>
    </button>
  );
}
